import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ClapDetector } from './game/clap';
import { BUCKET_KINDS, EARN_EVERY, MODES, MODE_KEYS, SUPER_PRICES, TARGET_EMOJI, THROWABLES, THROW_KINDS, TUNING, canFire, clapOff, createWorld, drain, launch, leftovers, setItem, step, tradeForSuper, type Pairing, type ThrowKind, type World } from './game/engine';
import { attachInput } from './game/input';
import { makeView, swayAt } from './game/project';
import { Renderer } from './game/render';
import { mulberry32 } from './game/rng';
import { claimName, fetchBoard, flushPending, putInventory, renamePlayer, submitScore, type Board } from './net/api';
import { loadBest, loadPlayer, recordBest, savePlayer } from './player';
import { SEAS, Sfx, oceanChoice, rollSea } from './ui/audio';
import { clear, el, toast } from './ui/dom';
import { Buzz } from './ui/haptics';
import { Mic } from './ui/mic';
import { renderBoard } from './ui/leaderboard';
import { renderStats } from './ui/stats';

/**
 * Three screens: title → play → result. No router yet; a single game.
 */
/** "🪨🥥 5  🐟🐢 1": landings by what was thrown at what */
function tallyText(tally: World['tally']): string {
  return (Object.entries(tally) as [Pairing, number][])
    .sort((a, b) => b[1] - a[1])
    .map(([key, n]) => {
      const [kind, target] = key.split(':') as [keyof typeof THROWABLES, keyof typeof TARGET_EMOJI];
      return `${THROWABLES[kind].emoji}${TARGET_EMOJI[target]}${n}`;
    })
    .join('  ');
}

/** what Zach (film major) says on a streak milestone or a long hit */
const ZACH_LINES = [
  'Great shot, kid. That was one in a million.',
  'This is the way.',
  "Roads? Where we're going, we don't need roads.",
  "That's no moon. That's a coconut.",
  'Hold on to your butts.',
  "He's beginning to believe.",
  'Make it so.',
  'Multipass!',
  'Nuke it from orbit. Only way to be sure.',
  'The spice must flow.',
  "I've seen things you people wouldn't believe.",
  'Hasta la vista, coconut.',
  'Absolute cinema.',
];
/** Zach's recordings, by line index in ZACH_LINES */
const ZACH_VOICE = ZACH_LINES.map((_, i) => `z-cheer-${String(i + 1).padStart(2, '0')}`);
/** what Annabelle says after every third miss in a row */
const HECKLES = [
  'you low key failed',
  'no rizz, no coconuts',
  "it's giving... miss",
  'skill issue',
  'bro.',
  'the coconut is RIGHT there',
  'that was not the vibe',
  "I'm crying 💀",
  'main character energy, zero hits',
];
const app = document.getElementById('app')!;
const player = loadPlayer();
const sfx = new Sfx();
const buzz = new Buzz();
const mic = new Mic();
sfx.enabled = player.sound;
buzz.enabled = player.haptics;
document.addEventListener('pointerdown', () => sfx.unlock(), { capture: true, passive: true });

let teardown: (() => void) | null = null;
let current: 'title' | 'play' | 'board' | 'stats' = 'title';

function show(screen: 'title' | 'play' | 'board' | 'stats', build: () => () => void): void {
  teardown?.();
  clear(app);
  document.body.dataset.screen = screen;
  current = screen;
  teardown = build();
}

/** the rules, as a sheet over the title, so the title itself fits on one screen */
function howToSheet(): void {
  const wrap = el(
    'div',
    { class: 'sheet-wrap' },
    el(
      'div',
      { class: 'sheet' },
      el('h2', {}, 'How to play'),
      el(
        'div',
        { class: 'howto' },
        el('div', {}, el('b', {}, 'Aim'), el('span', {}, 'Press on the water. Column = left/right, row = angle: low is flat, the horizon is 45°, the sky is a lob.')),
        el('div', {}, el('b', {}, 'Throw'), el('span', {}, 'Hold for power, let go to throw. Everything flies a real arc.')),
        el('div', {}, el('b', {}, 'Bucket'), el('span', {}, 'Rock, pineapple, fish, shoe. Each flies and pays differently. Shoe has the biggest splash.')),
        el('div', {}, el('b', {}, 'Friends'), el('span', {}, `Turtles, snorkelers, pelicans. Hit one and it's a strike; ${TUNING.strikesToEnd} end the round. A fish is a gift to a turtle or a pelican, a shoe sends a snorkeler off.`)),
        el('div', {}, el('b', {}, 'Shark'), el('span', {}, 'If it turns on the boat, land anything on it. A fish anywhere near it works. Reach the hull and it costs points and a limb.')),
        el('div', {}, el('b', {}, 'Whittie'), el('span', {}, 'A Whitman grad floats by on a pool float, tossing their cap. Only a shoe gets them (Go Blues, points). Anything else is a strike. Knock the cap out of the air for a bonus.')),
        el('div', {}, el('b', {}, 'Monkey'), el('span', {}, 'A capuchin paddles out on a log for your fish (Bro sometimes, OG often). Tip the log with anything, throw it a pineapple and it leaves happy, or clap twice (the guide was right; needs the microphone, only while a monkey is out). Throw it a fish and it keeps coming. Aboard, it takes 3 fish, or 2 pineapples, or a shoe.')),
        el('div', {}, el('b', {}, 'Clock'), el('span', {}, `${TUNING.duration} s, and every coconut adds ${TUNING.timePerCoconut}. Far coconuts pay far more.`)),
        el('div', { class: 'keys' }, el('b', {}, 'Keys'), el('span', {}, 'A/D or ←/→ bearing, W/S or ↑/↓ angle, 1–4 bucket, hold Space to throw, Esc to pause.')),
      ),
      el('button', { class: 'btn primary big', type: 'button', onclick: () => wrap.remove() }, 'Got it'),
    ),
  );
  app.append(wrap);
}

function title(): () => void {
  const top = el('div', { class: 'top' });
  let topFor: string | null = null;
  const loadTop = async (): Promise<void> => {
    const m = MODES[player.mode];
    if (!m.game) {
      top.replaceChildren(el('p', { class: 'dim center' }, 'Practice: no clock, nothing posted.'));
      return;
    }
    const game = m.game;
    topFor = game;
    const mine = loadBest(player.mode);
    top.replaceChildren(el('p', { class: 'dim center' }, 'Loading the board…'));
    const b = await fetchBoard(player.id, 3, game);
    if (topFor !== game) return; // the mode changed while loading
    if (!b) {
      top.replaceChildren(el('p', { class: 'dim center' }, mine && mine.score > 0 ? `Offline. Your ${m.label} best here: ${mine.score}.` : 'Offline.'));
      return;
    }
    const rows = b.entries.map((e, i) =>
      el('div', { class: 'top-row' + (e.player === player.id ? ' me' : '') }, el('span', { class: 'rank' }, ['🥇', '🥈', '🥉'][i]), el('span', { class: 'name' }, e.name), el('span', { class: 'score' }, String(e.score))),
    );
    const me = b.me && b.me.rank > 3 ? el('p', { class: 'dim center' }, `You: #${b.me.rank} of ${b.me.players}, best ${b.me.best}`) : null;
    top.replaceChildren(el('div', { class: 'top-title dim' }, `${m.label} top 3`), ...(rows.length ? rows : [el('p', { class: 'dim center' }, 'No one yet. Be the first.')]), ...(me ? [me] : []));
  };
  const modeBtns = MODE_KEYS.map((m) => {
    const b = el('button', { class: 'seg', type: 'button' }, MODES[m].label) as HTMLButtonElement;
    b.onclick = () => {
      player.mode = m;
      savePlayer(player);
      paintMode();
    };
    return b;
  });
  const paintMode = (): void => {
    MODE_KEYS.forEach((m, i) => modeBtns[i].classList.toggle('on', player.mode === m));
    void loadTop();
  };
  paintMode();
  const clapToggle = (): HTMLButtonElement => {
    const label = (): string => `Clap: ${player.clap === 'on' ? 'on' : player.clap === 'off' ? 'off' : 'ask'}`;
    const b = el('button', { class: 'btn ghost', title: 'clap twice at a monkey to send it away (uses the microphone while a monkey is out)' }, label());
    if (!Mic.supported()) b.hidden = true;
    b.onclick = () => {
      player.clap = player.clap === 'on' ? 'off' : 'on';
      savePlayer(player);
      b.textContent = label();
    };
    return b;
  };
  const toggle = (key: 'sound' | 'haptics', label: string): HTMLButtonElement => {
    const b = el('button', { class: 'btn ghost' }, `${label}: ${player[key] ? 'on' : 'off'}`);
    b.onclick = () => {
      player[key] = !player[key];
      savePlayer(player);
      sfx.enabled = player.sound;
      buzz.enabled = player.haptics;
      b.textContent = `${label}: ${player[key] ? 'on' : 'off'}`;
    };
    return b;
  };
  app.append(
    el(
      'div',
      { class: 'page' },
      el('div', { class: 'title' }, el('div', { class: 'title-row' }, el('h1', {}, 'Cocoshot'), el('img', { class: 'title-coco', src: '/sprites/coconut-1.png', alt: 'a coconut from the beach' })), el('p', { class: 'tag' }, 'Throw things at the coconuts. Mind the turtles.')),
      el('div', { class: 'modes' }, el('div', { class: 'segs' }, ...modeBtns)),
      top,
      el('p', { class: 'dim center specials' }, bagText(), ' ', el('button', { class: 'link', type: 'button', onclick: () => tradeSheet(() => show('title', title)) }, 'Trade')),
      el('div', { class: 'spacer' }),
      el('div', { class: 'row' }, toggle('sound', 'Sound'), toggle('haptics', 'Haptics'), clapToggle()),
      el('button', { class: 'btn primary big', onclick: () => show('play', play) }, 'Play'),
      el('div', { class: 'row' }, el('button', { class: 'btn ghost', onclick: () => howToSheet() }, 'Rules'), el('button', { class: 'btn ghost', onclick: () => show('board', board) }, 'Leaderboard'), el('button', { class: 'btn ghost', onclick: () => show('stats', stats) }, 'Stats')),
      /Android/i.test(navigator.userAgent) && !Capacitor.isNativePlatform()
        ? el('p', { class: 'dim center apk' }, el('a', { href: '/cocoshot.apk', class: 'link' }, 'Get the Android app'), ' (a 7 MB file; Android will ask you to allow installs from Chrome). Or add this page to your home screen.')
        : null,
      el('p', { class: 'credit dim' }, 'Feed music: "Run Amok" by Kevin MacLeod (incompetech.com), CC BY 4.0. Sloth icon: Twemoji, CC BY 4.0.'),
      el(
        'p',
        { class: 'who dim center' },
        player.signedIn ? `Signed in as ${player.name}. ` : player.name ? `Playing as ${player.name} on this phone. ` : 'No name yet. ',
        el('button', { class: 'link', type: 'button', onclick: () => (player.signedIn ? (signOut(), show('title', title)) : signInSheet(app, { onDone: (ok) => ok && show('title', title) })) }, player.signedIn ? 'Sign out' : 'Sign in or reserve a name'),
      ),
    ),
  );
  return () => {};
}

/**
 * The sign-in sheet: a name and a 4-character PIN. An unclaimed name is reserved for this device's
 * player (its scores come along); a reserved name signs in from anywhere. `onDone` gets the outcome.
 */
function signInSheet(host: HTMLElement, opts: { name?: string; reason?: string; onDone(ok: boolean): void }): void {
  const nameBox = el('input', { class: 'name', type: 'text', maxlength: '24', placeholder: 'name', autocomplete: 'nickname', value: opts.name ?? player.name }) as HTMLInputElement;
  const pinBox = el('input', { class: 'name pin', type: 'text', maxlength: '4', placeholder: 'PIN, 4 letters or digits', autocomplete: 'off', autocapitalize: 'none', inputmode: 'text' }) as HTMLInputElement;
  const msg = el('p', { class: 'dim' }, opts.reason ?? 'Reserve your name with a PIN and your stats follow you to any phone. Already have one? Same box.');
  const go = el('button', { class: 'btn primary big', type: 'button' }, 'Sign in / reserve') as HTMLButtonElement;
  const wrap = el('div', { class: 'sheet-wrap' }, el('div', { class: 'sheet' }, el('h2', {}, 'Your name'), msg, nameBox, pinBox, go, el('button', { class: 'btn ghost', type: 'button', onclick: () => { wrap.remove(); opts.onDone(false); } }, 'Not now')));
  host.append(wrap);
  const submit = async (): Promise<void> => {
    const name = nameBox.value.trim();
    const pin = pinBox.value.trim();
    if (!name || !/^[a-z0-9]{4}$/i.test(pin)) {
      msg.textContent = 'A name, and a PIN of exactly 4 letters or digits.';
      return;
    }
    go.disabled = true;
    const r = await claimName(name, pin, player.anonId);
    go.disabled = false;
    if (r === 'wrong') {
      msg.textContent = 'That name is reserved and the PIN does not match.';
      return;
    }
    if (r === 'bad' || r === null) {
      msg.textContent = r === null ? 'Could not reach the server. Try again when online.' : 'The PIN must be 4 letters or digits.';
      return;
    }
    player.id = r.player;
    player.name = r.name;
    player.signedIn = true;
    // a fresh reservation keeps what this device earned; a sign-in takes the account's store
    if (r.claimed) void putInventory(player.id, player.specials);
    else player.specials = { rock: 0, pineapple: 0, fish: 0, shoe: 0, super: 0, ...(r.inventory ?? {}) };
    savePlayer(player);
    wrap.remove();
    opts.onDone(true);
  };
  go.onclick = () => void submit();
  pinBox.onkeydown = (e) => {
    if (e.key === 'Enter') void submit();
  };
  (opts.name ? pinBox : nameBox).focus();
}

/** keep the account's earned items on the server when signed in */
function syncSpecials(): void {
  savePlayer(player);
  if (player.signedIn) void putInventory(player.id, player.specials);
}

/** the trade sheet: swap things from the bag for a super pineapple */
function tradeSheet(onChange: () => void): void {
  const line = el('p', { class: 'dim center' });
  const rows = el('div', { class: 'trades' });
  const paint = (): void => {
    line.textContent = bagText();
    rows.replaceChildren(
      ...(Object.keys(SUPER_PRICES) as ThrowKind[]).map((kind) => {
        const price = SUPER_PRICES[kind]!;
        const can = tradeForSuper(player.specials, kind) !== null;
        const plural = kind === 'fish' ? 'fish' : `${THROWABLES[kind].label}s`;
        const b = el('button', { class: 'btn trade', type: 'button', disabled: !can }, `${price} ${THROWABLES[kind].emoji} ${plural}  →  1 🍍✨`) as HTMLButtonElement;
        b.onclick = () => {
          const next = tradeForSuper(player.specials, kind);
          if (!next) return;
          player.specials = next;
          syncSpecials();
          sfx.click();
          paint();
          onChange();
        };
        return b;
      }),
    );
  };
  paint();
  const wrap = el('div', { class: 'sheet-wrap' }, el('div', { class: 'sheet' }, el('h2', {}, 'Trade for a super pineapple'), el('p', { class: 'dim' }, 'A super pineapple homes in on the nearest target and cannot miss. Trade from your bag; the free bucket each round is not for sale.'), line, rows, el('button', { class: 'btn ghost', type: 'button', onclick: () => wrap.remove() }, 'Done')));
  app.append(wrap);
}

/** "In your bag: 🪨 2 · 🐟 1 · 🍍✨ 1", or the pitch when it is empty */
function bagText(): string {
  const parts = THROW_KINDS.filter((k) => player.specials[k] > 0).map((k) => `${THROWABLES[k].emoji}${k === 'super' ? '✨' : ''} ${player.specials[k]}`);
  return parts.length ? `In your bag for the next round: ${parts.join(' · ')}` : `Feed Me earns extras: every mouth fed is something for next time, the ${EARN_EVERY}th a super pineapple that homes in and cannot miss.`;
}

function signOut(): void {
  player.id = player.anonId;
  player.signedIn = false;
  player.name = '';
  savePlayer(player);
}

function stats(): () => void {
  return renderStats(app, player, { back: () => show('title', title), signIn: () => signInSheet(app, { onDone: () => show('stats', stats) }) });
}

function board(): () => void {
  return renderBoard(app, player, { back: () => show('title', title), play: () => show('play', play) });
}

/** post a finished round; the board comes back (null offline or if the player has no name yet) */
function postRound(world: World): Promise<Board | null | 'reserved'> {
  const game = MODES[world.mode].game;
  if (!game) return Promise.resolve(null);
  // no name yet: the round still goes on the board, as Anonymous, under this device's id
  return submitScore({
    game,
    player: player.id,
    name: player.name || 'Anonymous',
    score: world.score,
    mistakes: world.strikes,
    detail: { hits: world.hits, farthest: Math.round(world.farthest), shots: world.shots, tally: world.tally, distance: Math.round(world.distance), seconds: Math.round(world.t) },
  });
}

function play(): () => void {
  const seed = (Date.now() ^ (Math.random() * 1e9)) >>> 0;
  const rng = mulberry32(seed);
  const brought = { ...player.specials };
  const world = createWorld(rng, player.mode, brought);
  // whatever comes back out of the round (unthrown extras, new earnings) goes in the bag when the round ends or is quit
  let bagged = false;
  const bag = (): void => {
    if (bagged || !MODES[player.mode].game) return;
    bagged = true;
    player.specials = leftovers(world, brought);
    syncSpecials();
  };
  const canvas = el('canvas');
  const score = el('div', { class: 'score' }, '0');
  const streak = el('div', { class: 'streak' }, '');
  const time = el('div', { class: 'time' }, Number.isFinite(MODES[player.mode].duration) ? String(MODES[player.mode].duration) : '∞');
  const bonus = el('div', { class: 'bonus' }, '');
  const power = el('div', { class: 'power' }, '');
  const tallyEl = el('div', { class: 'tally' }, '');
  const strikes = el('div', { class: 'strikes' });
  // the little figure the shark eats, one limb per bite
  const figure = el('div', { class: 'figure', title: 'you, so far' });
  figure.innerHTML = `<svg viewBox="0 0 24 40" width="18" height="30" aria-hidden="true">
    <circle cx="12" cy="6" r="5" fill="#fff"/>
    <line x1="12" y1="11" x2="12" y2="24" stroke="#fff" stroke-width="3" stroke-linecap="round"/>
    <line class="limb arm-l" x1="12" y1="14" x2="3" y2="21" stroke="#fff" stroke-width="3" stroke-linecap="round"/>
    <line class="limb arm-r" x1="12" y1="14" x2="21" y2="21" stroke="#fff" stroke-width="3" stroke-linecap="round"/>
    <line class="limb leg-l" x1="12" y1="24" x2="5" y2="37" stroke="#fff" stroke-width="3" stroke-linecap="round"/>
    <line class="limb leg-r" x1="12" y1="24" x2="19" y2="37" stroke="#fff" stroke-width="3" stroke-linecap="round"/>
  </svg>`;
  const paintFigure = (): void => {
    const gone = ['leg-l', 'leg-r', 'arm-l', 'arm-r'].slice(0, world.bites);
    for (const c of ['leg-l', 'leg-r', 'arm-l', 'arm-r']) figure.querySelector(`.${c}`)!.classList.toggle('gone', gone.includes(c));
  };
  const flash = el('div', { class: 'flash' });
  const oopsImg = el('img', { src: '/oops.jpg', alt: '' });
  const oops = el('div', { class: 'oops' }, oopsImg, el('div', { class: 'oops-text' }, ''));
  // preload the other faces so the first pop doesn't flash empty
  for (const src of ['/zach.jpg', '/pair-frown.jpg']) new Image().src = src;
  const quit = el('button', { class: 'quit', onclick: () => show('title', title) }, '✕');
  const pauseBtn = el('button', { class: 'pause-btn', 'aria-label': 'pause' }, '❚❚') as HTMLButtonElement;
  const countdown = el('div', { class: 'countdown' }, '');
  const pauseOverlay = el(
    'div',
    { class: 'pause-overlay', hidden: true },
    el('div', { class: 'pause-card' }, el('h2', {}, 'Paused'), el('button', { class: 'btn primary big', type: 'button' }, 'Resume'), el('button', { class: 'btn ghost', type: 'button', onclick: () => show('title', title) }, 'Quit')),
  );
  let paused = false;
  const setPaused = (on: boolean): void => {
    if (world.over || paused === on) return;
    paused = on;
    pauseOverlay.hidden = !on;
    pauseBtn.textContent = on ? '▶' : '❚❚';
    sfx.ambient(!on && !feedRound);
    sfx.music(!on && feedRound);
    if (!on) last = null; // don't count the paused time
  };
  pauseBtn.onpointerdown = (e) => {
    e.stopPropagation();
    setPaused(!paused);
  };
  (pauseOverlay.querySelector('.btn.primary') as HTMLButtonElement).onclick = () => setPaused(false);
  // the bucket: one button per thing to throw
  const itemBtns = THROW_KINDS.map((kind) => {
    const b = el('button', { class: kind === 'super' ? 'item super' : 'item', 'aria-label': THROWABLES[kind].label, title: THROWABLES[kind].label }, THROWABLES[kind].emoji, el('span', { class: 'count' }, '')) as HTMLButtonElement;
    b.onpointerdown = (e) => {
      e.stopPropagation();
      pick(kind);
    };
    return b;
  });
  const bucket = el('div', { class: 'bucket' }, ...itemBtns);
  const paintBucket = (): void => {
    THROW_KINDS.forEach((kind, i) => {
      const b = itemBtns[i];
      b.hidden = kind === 'super' && world.ammo.super <= 0 && world.item !== 'super';
      b.classList.toggle('on', world.item === kind);
      b.classList.toggle('empty', world.ammo[kind] <= 0);
      const n = String(world.ammo[kind]);
      const badge = b.querySelector('.count')!;
      if (badge.textContent !== n) badge.textContent = n;
    });
  };
  const pick = (kind: (typeof THROW_KINDS)[number]): void => {
    if (setItem(world, kind)) paintBucket();
  };
  const feedRound = !!MODES[player.mode].feed;
  const root = el(
    'div',
    { class: feedRound ? 'play feed' : 'play' },
    canvas,
    el('div', { class: 'hud' }, el('div', { class: 'hud-left' }, el('div', {}, score, streak), tallyEl), el('div', { class: 'center' }, time, bonus, power), el('div', { class: 'hud-right' }, strikes, figure)),
    flash,
    oops,
    countdown,
    quit,
    pauseBtn,
    bucket,
    pauseOverlay,
  );
  // the sea (a different one each round) for the length of the round, or the chase tune in the Feed round;
  // Zach's lines warm up in the background
  rollSea();
  sfx.ambient(!feedRound);
  sfx.music(feedRound);
  sfx.preloadVoices([...ZACH_VOICE, 'z-turtle-fish', 'z-pelican-fish', 'z-shark-fish', 'z-shark-rock', 'z-shark-pineapple', 'z-shark-shoe', 'z-snorkeler-shoe']);
  app.append(root);

  const timers: number[] = [];
  const clapper = new ClapDetector();
  let listening = false;
  const askedThisRound = { done: false };
  const startListening = async (): Promise<void> => {
    if (listening || player.clap !== 'on') return;
    listening = await mic.open();
    if (!listening && mic.state === 'denied') {
      player.clap = 'off';
      savePlayer(player);
      toast(root, 'no microphone, so no clapping. throw a pineapple instead.', 'info', 1600);
    }
  };
  const stopListening = (): void => {
    if (!listening) return;
    mic.close();
    listening = false;
  };
  /** the first monkey of a round: offer clapping once if the player hasn't decided */
  const offerClap = (): void => {
    if (player.clap !== 'ask' || askedThisRound.done || !Mic.supported()) return;
    askedThisRound.done = true;
    const wrap = el(
      'div',
      { class: 'sheet-wrap' },
      el(
        'div',
        { class: 'sheet' },
        el('h2', {}, 'Clap at the monkey'),
        el('p', {}, 'The guide on the hike said: if a monkey comes for you, clap and it goes away. The game can listen for two claps while a monkey is paddling out, and send it off for the same points as tipping its log.'),
        el('p', { class: 'dim' }, 'It only listens while a monkey is out. Nothing is recorded or sent anywhere; the sound is turned into a loudness number on your phone and thrown away.'),
        el('button', { class: 'btn primary big', type: 'button', onclick: () => { wrap.remove(); player.clap = 'on'; savePlayer(player); setPaused(false); void startListening(); } }, 'Let me clap'),
        el('button', { class: 'btn ghost', type: 'button', onclick: () => { wrap.remove(); player.clap = 'off'; savePlayer(player); setPaused(false); } }, 'No thanks'),
      ),
    );
    setPaused(true);
    pauseOverlay.hidden = true;
    root.append(wrap);
  };
  const renderer = new Renderer(canvas);
  // a probe whose padding is the safe-area inset, so the canvas can keep the arm above a system bar
  const probe = el('div', { class: 'inset-probe', 'aria-hidden': 'true' });
  root.append(probe);
  let view = makeView(1, 1);
  const resize = (): void => {
    const r = root.getBoundingClientRect();
    const insetBottom = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
    view = makeView(r.width, r.height);
    renderer.resize(r.width, r.height, insetBottom);
  };
  resize();
  window.addEventListener('resize', resize);
  // the native shell publishes its insets shortly after load; re-measure a few times
  for (const ms of [250, 1000, 2500]) timers.push(window.setTimeout(resize, ms));

  const paintStrikes = (): void => {
    clear(strikes);
    for (let i = 0; i < TUNING.strikesToEnd; i++) strikes.append(el('span', { class: i < world.strikes ? 'on' : '' }, '●'));
  };
  paintStrikes();

  const input = attachInput(canvas, {
    canAim: () => canFire(world),
    onFire: (aim) => launch(world, aim.azimuth, aim.elevation, aim.power),
    view: () => view,
    onItem: (which) => {
      const i = which === 'next' ? (BUCKET_KINDS.indexOf(world.item) + 1) % BUCKET_KINDS.length : which;
      if (THROW_KINDS[i]) pick(THROW_KINDS[i]);
    },
  });
  paintBucket();

  let last: number | null = null;
  let raf = 0;
  let shownScore = -1;
  let shownTime = -1;
  let shownStreak = -1;
  let flashTimer = 0;
  let oopsTimer = 0;

  const frame = (now: number): void => {
    raf = requestAnimationFrame(frame);
    if (last === null) {
      last = now;
      return;
    }
    const dt = Math.min(1 / 30, (now - last) / 1000);
    last = now;
    if (!paused) step(world, view, dt, rng);
    if (listening && !paused) {
      if (world.monkeys.some((m) => m.state === 'coming')) {
        if (clapper.sample(mic.level(), world.t)) clapOff(world);
      } else {
        stopListening();
      }
    }
    swayAt(view, world.t);
    const aim = input.current(now);
    renderer.draw(world, view, aim);
    const powerText = aim?.charging ? `${Math.round(aim.power * 100)}%` : '';
    if (power.textContent !== powerText) power.textContent = powerText;

    for (const e of drain(world)) handle(e);
    if (world.score !== shownScore) {
      shownScore = world.score;
      score.textContent = String(world.score);
    }
    if (Number.isFinite(world.duration)) {
      const left = Math.max(0, Math.ceil(world.duration - world.t));
      if (left !== shownTime) {
        shownTime = left;
        time.textContent = String(left);
        // the last five seconds, large and faint over the water, with a tick
        if (left > 0 && left <= 5 && !world.over) {
          countdown.textContent = String(left);
          countdown.classList.remove('show');
          void countdown.offsetWidth;
          countdown.classList.add('show');
          sfx.tick();
        } else if (countdown.textContent) {
          countdown.textContent = '';
          countdown.classList.remove('show');
        }
      }
    }
    if (world.streak !== shownStreak) {
      shownStreak = world.streak;
      streak.textContent = world.streak >= 2 ? `streak ×${world.streak}` : '';
    }
    const tt = tallyText(world.tally);
    if (tallyEl.textContent !== tt) tallyEl.textContent = tt;
  };

  const showOops = (text: string, ms: number, who: 'annabelle' | 'zach' | 'pair' = 'annabelle', voice?: string): void => {
    // Feed Me: the target is in the middle of the screen, so faces stay out of it; a line at the top instead
    if (feedRound) {
      toast(root, text, who === 'zach' ? 'ok' : 'bad', Math.min(ms, 1400));
      if (voice) void sfx.say(voice);
      return;
    }
    if (voice) {
      void sfx.say(voice).then((len) => {
        if (len > ms - 200 && oops.classList.contains('show')) {
          clearTimeout(oopsTimer);
          oopsTimer = window.setTimeout(() => oops.classList.remove('show'), len + 300);
        }
      });
    }
    oopsImg.src = who === 'zach' ? '/zach.jpg' : who === 'pair' ? '/pair-frown.jpg' : '/oops.jpg';
    oops.classList.toggle('zach', who === 'zach');
    oops.classList.toggle('pair', who === 'pair');
    oops.querySelector('.oops-text')!.textContent = text;
    oops.classList.remove('show');
    void oops.offsetWidth; // restart the pop animation
    oops.classList.add('show');
    clearTimeout(oopsTimer);
    oopsTimer = window.setTimeout(() => oops.classList.remove('show'), ms);
  };
  let heckles = 0;
  let cheers = 0;
  const handle = (e: ReturnType<typeof drain>[number]): void => {
    switch (e.type) {
      case 'fire':
        sfx.throwRock();
        clapper.mute(world.t + 0.25);
        buzz.light();
        paintBucket();
        break;
      case 'earned':
        if (e.what === 'super') {
          sfx.win();
          buzz.success();
          showOops(`${e.fed} fed: a super pineapple is yours. it homes in and cannot miss.`, 1800, 'zach');
        } else {
          buzz.light();
          toast(root, `+1 ${THROWABLES[e.what].label} for next time ${THROWABLES[e.what].emoji}`, 'ok', 1100);
        }
        break;
      case 'fed':
        sfx.munch();
        buzz.success();
        break;
      case 'bonk':
        sfx.crack();
        buzz.light();
        if (e.who === 'annabelle') showOops('you hit my face. not slay.', 1200);
        else if (e.who === 'zach') showOops('That was my face, kid.', 1200, 'zach');
        else toast(root, 'bonk. the mouth, not the hat.', 'info', 1100);
        break;
      case 'face':
        toast(root, e.who === 'ed' ? 'Ed' : e.who === 'zach' ? 'Zach' : 'Annabelle', 'info', 900);
        break;
      case 'goBlues':
        sfx.munch();
        buzz.success();
        showOops(`Go Blues! the Whittie caught the shoe. +${e.pts}`, 1400, 'zach');
        break;
      case 'capped':
        sfx.crack();
        buzz.success();
        showOops(`knocked the cap right out of the air. +${e.pts}`, 1300, 'zach');
        break;
      case 'monkey':
        sfx.chatter();
        clapper.mute(world.t + 0.6);
        buzz.heavy();
        if (player.metMonkey) toast(root, '🐒 murder monkey', 'bad', 1000);
        else {
          toast(root, player.clap === 'on' ? '🐒 murder monkey inbound. clap twice, tip the log, or throw it a pineapple' : '🐒 murder monkey inbound. it wants your fish. tip the log, or throw it a pineapple', 'bad', 2600);
          player.metMonkey = true;
          savePlayer(player);
        }
        offerClap();
        void startListening();
        break;
      case 'monkeyFed':
        sfx.chatter();
        toast(root, '🐒 it caught the fish and kept coming', 'bad', 1300);
        break;
      case 'monkeyOff':
        sfx.munch();
        buzz.success();
        showOops(e.how === 'clapped' ? `clapped the monkey off. the guide was right. +${e.pts}` : e.how === 'bribed' ? `the monkey took the pineapple and paddled off. +${e.pts}` : `tipped the monkey off its log. +${e.pts}`, 1400, 'zach');
        break;
      case 'robbed':
        sfx.chomp();
        buzz.error();
        paintBucket();
        showOops(e.n ? `the monkey got aboard and took ${e.n} ${THROWABLES[e.took].label}${e.n === 1 ? '' : e.took === 'fish' ? '' : 's'}.` : 'the monkey got aboard. nothing worth taking.', 1700, 'pair');
        flash.classList.add('on');
        clearTimeout(flashTimer);
        flashTimer = window.setTimeout(() => flash.classList.remove('on'), 60);
        break;
      case 'bonus':
        bonus.textContent = `+${e.seconds}s`;
        bonus.classList.remove('show');
        void bonus.offsetWidth;
        bonus.classList.add('show');
        break;
      case 'shoed':
        sfx.munch();
        buzz.success();
        showOops(`the snorkeler got the shoe. +${e.pts}`, 1300, 'zach', 'z-snorkeler-shoe');
        break;
      case 'incoming':
        sfx.jaws();
        buzz.heavy();
        toast(root, e.what === 'turtle' ? '🐢 a turtle is coming for the boat. fish!' : e.what === 'pelican' ? '🦤 pelican inbound. fish!' : '🤿 the snorkeler is coming aboard. shoe!', 'bad', 1600);
        break;
      case 'boarded':
        sfx.chomp();
        buzz.error();
        showOops(e.what === 'turtle' ? `the turtle rammed the hull. -${e.cost}` : e.what === 'pelican' ? `the pelican took your lunch. -${e.cost}` : `the snorkeler climbed aboard and took your drink. -${e.cost}`, 1700, 'pair');
        flash.classList.add('on');
        clearTimeout(flashTimer);
        flashTimer = window.setTimeout(() => flash.classList.remove('on'), 60);
        break;
      case 'empty':
        toast(root, e.next ? `out of ${THROWABLES[e.kind].label}s` : 'the bucket is empty', 'info', 1200);
        paintBucket();
        break;
      case 'item':
        sfx.click();
        buzz.light();
        break;
      case 'feed':
        sfx.munch();
        buzz.success();
        showOops(e.what === 'pelican' ? `the pelican caught the ${THROWABLES[e.kind].label}. +${e.pts}` : `the turtle took the ${THROWABLES[e.kind].label}. +${e.pts}`, 1300, 'zach', e.what === 'pelican' ? 'z-pelican-fish' : 'z-turtle-fish');
        break;
      case 'hit':
        sfx.crack();
        clapper.mute(world.t + 0.4);
        buzz.heavy();
        break;
      case 'miss':
        sfx.splash(e.d > 30);
        clapper.mute(world.t + 0.6);
        if (e.close) buzz.light();
        break;
      case 'charge':
        sfx.jaws();
        buzz.heavy();
        toast(root, '🦈 it\'s coming for the boat', 'bad', 1400);
        break;
      case 'chomp':
        sfx.chomp();
        buzz.error();
        paintFigure();
        showOops(`Sharks spoil EVERYTHING! there goes ${e.limb}. -${e.cost}`, 1800, 'pair');
        flash.classList.add('on');
        clearTimeout(flashTimer);
        flashTimer = window.setTimeout(() => flash.classList.remove('on'), 60);
        break;
      case 'repel':
        sfx.munch();
        buzz.success();
        showOops(e.fed ? `the shark took the fish and forgot about us. +${e.pts}` : `shark repelled with a ${THROWABLES[e.kind].label}. +${e.pts}`, 1500, 'zach', e.fed ? 'z-shark-fish' : `z-shark-${e.kind}`);
        break;
      case 'strike':
        sfx.strike();
        buzz.error();
        paintStrikes();
        showOops(e.what === 'snorkeler' ? 'you hit a snorkeler. not slay.' : e.what === 'pelican' ? 'you hit a pelican. not slay.' : e.what === 'grad' ? 'that was a Whittie. they have finals. not slay.' : 'you scared a turtle. not slay.', 1500, 'pair');
        flash.classList.add('on');
        clearTimeout(flashTimer);
        flashTimer = window.setTimeout(() => flash.classList.remove('on'), 60);
        break;
      case 'heckle':
        buzz.light();
        showOops(HECKLES[heckles++ % HECKLES.length], 1400);
        break;
      case 'cheer':
        buzz.success();
        showOops(ZACH_LINES[cheers % ZACH_LINES.length], 1500, 'zach', ZACH_VOICE[cheers % ZACH_LINES.length]);
        cheers++;
        break;
      case 'over':
        bag();
        sfx.win();
        sfx.ambient(false);
        sfx.music(false);
        countdown.textContent = '';
        timers.push(window.setTimeout(() => result(world, e.reason), e.reason === 'eaten' ? 1600 : 700));
        break;
    }
  };

  // ?debug: preview the faces and sounds without earning them (z = Zach, x = Annabelle, p = splash, k = knock, t = throw)
  const inField = (e: KeyboardEvent): boolean => {
    const t = e.target as HTMLElement | null;
    return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
  };
  const debugKeys = (e: KeyboardEvent): void => {
    if (!location.search.includes('debug') || inField(e)) return;
    if (e.key === 'z') {
      showOops(ZACH_LINES[cheers % ZACH_LINES.length], 1500, 'zach', ZACH_VOICE[cheers % ZACH_LINES.length]);
      cheers++;
    }
    if (e.key === 'x') showOops(HECKLES[heckles++ % HECKLES.length], 1400);
    if (e.key === 'p') sfx.splash(false);
    if (e.key === 'k') sfx.crack();
    if (e.key === 't') sfx.throwRock();
    if (e.key === 'e') world.duration = world.t + 0.01; // end the round now
    if (e.key === 'c') world.duration = world.t + 6; // jump to the last seconds
    if (e.key === 'o') {
      // cycle the sea: 1 = lfinck, 2 = davidjuradoh, 3 = yevgverh
      const next = String((SEAS.indexOf(oceanChoice()) + 1) % SEAS.length + 1);
      try {
        localStorage.setItem('coco.ocean', next);
      } catch {
        /* no storage */
      }
      sfx.ambient(false);
      window.setTimeout(() => sfx.ambient(true), 600);
      toast(root, `sea ${next}`, 'info', 900);
    }
  };
  window.addEventListener('keydown', debugKeys);

  const onVisibility = (): void => {
    // going to the background pauses the round
    if (document.hidden) setPaused(true);
  };
  const pauseKeys = (e: KeyboardEvent): void => {
    if (inField(e) || world.over) return;
    if (e.key === 'Escape' || e.key === 'p' || e.key === 'P') {
      e.preventDefault();
      setPaused(!paused);
    }
  };
  window.addEventListener('keydown', pauseKeys);
  document.addEventListener('visibilitychange', onVisibility);
  raf = requestAnimationFrame(frame);

  return () => {
    cancelAnimationFrame(raf);
    input.detach();
    window.removeEventListener('resize', resize);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('keydown', debugKeys);
    window.removeEventListener('keydown', pauseKeys);
    stopListening();
    bag();
    sfx.ambient(false);
    sfx.music(false);
    for (const t of timers) clearTimeout(t);
    clearTimeout(flashTimer);
    clearTimeout(oopsTimer);
  };
}

function result(world: World, reason: 'time' | 'strikes' | 'eaten'): void {
  const ranked = MODES[world.mode].game !== null;
  const newBest = ranked && recordBest(world.mode, world.score, world.hits, world.farthest);
  const accuracy = world.shots ? Math.round((100 * world.hits) / world.shots) : 0;
  // standing on the leaderboard, or a name box if the player hasn't got one yet
  const standing = el('p', { class: 'standing dim' }, !ranked ? 'Practice round, not posted.' : 'Posting…');
  const setStanding = (b: Board | null | 'reserved'): void => {
    if (b === 'reserved') {
      standing.textContent = `"${player.name}" is reserved with a PIN.`;
      standing.hidden = false;
      signInSheet(app, {
        name: player.name,
        reason: `"${player.name}" belongs to someone with a PIN. Enter it to post as them, or change the name.`,
        onDone: (ok) => {
          if (ok) void postRound(world).then(setStanding);
          else {
            player.name = '';
            savePlayer(player);
            standing.textContent = 'Not posted: pick another name next round.';
          }
        },
      });
      return;
    }
    if (b?.me) standing.textContent = `#${b.me.rank} of ${b.me.players} on the leaderboard${player.name ? '' : ' as Anonymous'}`;
    else standing.textContent = 'Offline. Your score will post when you are back online.';
    standing.hidden = !standing.textContent;
    if (b && !player.signedIn && player.name) reserveOffer.hidden = false;
  };
  const reserveOffer = el('button', { class: 'link', type: 'button', hidden: true, onclick: () => signInSheet(app, { name: player.name, onDone: (ok) => { if (ok) reserveOffer.hidden = true; } }) }, `Reserve "${player.name}" with a PIN so your stats follow you`);
  const zachSays =
    newBest || world.hits >= 10 || world.farthest >= 40
      ? newBest
        ? { text: 'Absolute cinema.', voice: 'z-verdict-best' }
        : world.farthest >= 40
          ? { text: `${Math.round(world.farthest)} metres. I felt that.`, voice: 'z-verdict-far-40' }
          : { text: 'Great shot, kid.', voice: 'z-verdict-great' }
      : null;
  if (zachSays) window.setTimeout(() => void sfx.say(zachSays.voice), 900);
  const nameBox = el('input', { class: 'name', type: 'text', maxlength: '24', placeholder: 'your name instead of Anonymous', autocomplete: 'nickname', enterkeyhint: 'done' }) as HTMLInputElement;
  const postBtn = el('button', { class: 'btn', type: 'button' }, 'Use name') as HTMLButtonElement;
  const nameRow = player.name || !ranked ? null : el('div', { class: 'row namerow' }, nameBox, postBtn);
  const naming = async (): Promise<void> => {
    const name = nameBox.value.trim();
    if (!name) return;
    player.name = name;
    savePlayer(player);
    nameRow?.remove();
    standing.textContent = 'Posting…';
    standing.hidden = false;
    void renamePlayer(player.id, name);
    setStanding(await postRound(world));
  };
  postBtn.onclick = () => void naming();
  nameBox.onkeydown = (e) => {
    if (e.key === 'Enter') void naming();
  };
  if (ranked) void postRound(world).then(setStanding);
  const verdict =
    reason === 'eaten'
      ? 'we told you about the shark.'
      : reason === 'strikes'
      ? 'three turtles. we are not speaking.'
      : newBest
        ? 'ok that actually slaps. W.'
        : world.hits === 0
          ? 'hand over the gun, bestie.'
          : world.hits >= 10
            ? 'lowkey cooked. run it back.'
            : 'mid. run it back.';
  const wrap = el(
    'div',
    { class: 'sheet-wrap' },
    el(
      'div',
      { class: 'sheet' },
      reason !== 'time' || world.hits === 0 ? el('img', { class: 'pair-dinner', src: '/pair-dinner.jpg', alt: '' }) : null,
      el(
        'div',
        { class: 'verdict' },
        el('img', { src: '/oops.jpg', alt: '' }),
        el('div', { class: 'verdict-text' }, el('p', { class: 'dim' }, `${MODES[world.mode].label} · ${reason === 'strikes' ? 'Three strikes.' : reason === 'eaten' ? 'Eaten.' : 'Time!'}`), el('p', {}, verdict)),
        el('div', { class: 'score-block' }, el('div', { class: 'big-score' }, String(world.score)), newBest ? el('div', { class: 'newbest' }, 'new best') : null),
      ),
      zachSays
        ? el('div', { class: 'verdict zach' }, el('img', { src: '/zach.jpg', alt: '' }), el('div', {}, el('p', { class: 'dim' }, 'Zach'), el('p', {}, zachSays.text)))
        : null,
      standing,
      reserveOffer,
      nameRow,
      el('button', { class: 'btn primary big', onclick: () => show('play', play) }, 'Play again'),
      Object.keys(world.tally).length ? el('p', { class: 'tally-line' }, tallyText(world.tally)) : null,
      el(
        'div',
        { class: 'stats' },
        el('div', { class: 'stat' }, el('b', {}, String(world.hits)), el('span', {}, 'coconuts')),
        el('div', { class: 'stat' }, el('b', {}, `${Math.round(world.farthest)} m`), el('span', {}, 'farthest')),
        el('div', { class: 'stat' }, el('b', {}, `${accuracy}%`), el('span', {}, 'accuracy')),
      ),
      el('div', { class: 'row' }, el('button', { class: 'btn ghost', onclick: () => show('board', board) }, 'Leaderboard'), el('button', { class: 'btn ghost', onclick: () => show('title', title) }, 'Back')),
    ),
  );
  app.append(wrap);
}

show('title', title);

// anything that couldn't post while offline
void flushPending();
window.addEventListener('online', () => void flushPending());

// the Android back button / iOS back gesture inside the shells: leave the round or the board, else exit
if (Capacitor.isNativePlatform()) {
  void CapApp.addListener('backButton', () => {
    if (current !== 'title') show('title', title);
    else void CapApp.exitApp();
  });
}

// offline shell after one visit; only in production builds and only over https
if (import.meta.env.PROD && 'serviceWorker' in navigator && location.protocol === 'https:') {
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js'));
}
